import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Sun, Moon } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const LOGO_URL =
  `${process.env.PUBLIC_URL}/images/logo.png`;

const NAV_ITEMS = [
  { key: "chiSiamo", id: "chi-siamo" },
  { key: "network", id: "network" },
  { key: "video", id: "video" },
  { key: "percheNoi", id: "perche-noi" },
  { key: "solutions", id: "soluzioni" },
  { key: "numeri", id: "numeri" },
];

function scrollToSection(id) {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -72 });
  } else {
    el.scrollIntoView({ behavior: "smooth" });
  }
}

export default function Navbar() {
  const { lang, toggle, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [theme, setTheme] = useState(
    () => document.documentElement.dataset.theme || localStorage.getItem("dp-theme") || "dark"
  );
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("dp-theme", theme);
  }, [theme]);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  const go = (id) => {
    setOpen(false);
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToSection(id), 700);
    } else {
      scrollToSection(id);
    }
  };

  return (
    <>
      <motion.header
        data-testid="site-navbar"
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        className={`fixed top-0 inset-x-0 z-50 transition-[background,border-color,padding] duration-500 ${
          scrolled || open
            ? "bg-black/70 backdrop-blur-xl border-b border-white/10 py-4"
            : "bg-transparent border-b border-transparent py-6"
        }`}
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 flex items-center justify-between gap-6">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
            <img
              src={LOGO_URL}
              alt="Delta Pictures"
              className="w-8 h-8 md:w-9 md:h-9 object-contain group-hover:rotate-6 transition-transform duration-500"
              draggable="false"
            />
            <span className="font-display text-[18px] md:text-[20px] leading-none tracking-tight text-white font-semibold">
              Delta Pictures
            </span>
          </Link>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setTheme((v) => (v === "dark" ? "light" : "dark"))}
              data-testid="theme-toggle"
              className="w-10 h-10 flex items-center justify-center border border-white/15 text-white hover:bg-white hover:text-black transition-colors duration-300"
              aria-label={theme === "dark" ? "Attiva tema chiaro" : "Attiva tema scuro"}
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>

            <button
              onClick={toggle}
              data-testid="lang-toggle"
              className="hidden md:flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] text-neutral-400 hover:text-white transition-colors"
              aria-label="Toggle language"
            >
              <span className={lang === "it" ? "text-white" : ""}>IT</span>
              <span className="text-neutral-600">/</span>
              <span className={lang === "en" ? "text-white" : ""}>EN</span>
            </button>

            <button
              onClick={() => setOpen((v) => !v)}
              className="w-10 h-10 flex items-center justify-center border border-white/15 text-white hover:bg-white hover:text-black transition-colors duration-300"
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 bg-black pt-24 pb-10 px-6 md:px-10 flex flex-col justify-between"
          >
            <nav className="flex flex-col gap-6 mt-6 max-w-[1600px] w-full mx-auto">
              {NAV_ITEMS.map((n, i) => (
                <motion.div
                  key={n.key}
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.1 + i * 0.06, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  <button
                    onClick={() => go(n.id)}
                    className="font-display text-4xl tracking-tight text-neutral-300 hover:text-white transition-colors text-left"
                    data-testid={`mobile-nav-${n.key}`}
                  >
                    {t.nav[n.key]}
                  </button>
                </motion.div>
              ))}
            </nav>
            <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.22em] text-neutral-500 max-w-[1600px] w-full mx-auto">
              <button onClick={toggle} data-testid="mobile-lang-toggle" className="text-white">
                {lang === "it" ? "IT / en" : "it / EN"}
              </button>
              <span>Delta Pictures — {new Date().getFullYear()}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
